/** @module primary-nav */

import { eventListenerOptions } from '@spinnwerk/polyfills';
import $ from 'jquery';

/**
 * Handles functionality of the primary navigation component.
 * Above a given breakpoint, it acts as a dropdown-menu, below an accordion-menu.
 * As a dropdown-menu, all submenus delay closing for a given duration, except when other submenus are opened.
 * As an accordion-menu, opening and closing happens immediately, but also only one submenu of the same level can be open concurrently.
 *
 * Known issues:
 * * If the viewport is rather small, the expanded submenus may exceed the viewport.
 *
 * @see https://www.w3.org/WAI/tutorials/menus/flyout/#use-button-as-toggle
 * @see https://www.w3.org/TR/wai-aria-practices/examples/disclosure/disclosure-navigation.html
 */
export class PrimaryNav {
    /**
     * @param {object} [options={}] - Configuration options.
     * @param {Element|string} [options.primaryNav=null] - Either the element or the selector for the element that
     *  holds the primary navigation component. If omitted, the element is determined by selector
     *  <code>[data-primary-nav]</code>.
     * @param {string} [options.breakpoint='medium'] - Breakpoint to switch between dropdown- and accordion-menu.
     * @param {number} [options.closingDelay=1000] - Delay in <code>ms</code> for how long the closing of submenus should be suspended.
     */
    constructor({ primaryNav = null, breakpoint = 'medium', closingDelay = 1000 } = {}) {
        /** @type {Element} */
        this._primaryNav = null;

        if (primaryNav && primaryNav instanceof Element) {
            this._primaryNav = primaryNav;
        } else if (primaryNav && typeof primaryNav === 'string') {
            this._primaryNav = document.querySelector(primaryNav);
        } else {
            this._primaryNav = document.querySelector('[primary-nav]');
        }

        if (breakpoint) {
            /** @type {string} */
            this._breakpoint = breakpoint;
        }

        if (closingDelay) {
            /** @type {number} */
            this._closingDelay = closingDelay;
        }

        /** @type {Element[]} */
        this._subMenus = null;

        /** @type {Set} */
        this._openMenus = new Set();

        /** @type {WeakMap<Element,number>} */
        this._subMenuClosingTimeouts = new WeakMap();

        /** @type {boolean} */
        this._touch = false;

        this._init();
    }

    /** @private */
    _init() {
        const listenerOptions = eventListenerOptions({ passive: true });

        this._primaryNav.querySelectorAll('[data-menu-item][data-has-submenu]').forEach((menuItem) => {
            menuItem.addEventListener('touchstart', this._onSubmenuParentTouchStart.bind(this), listenerOptions);

            menuItem.addEventListener('mouseenter', this._onSubmenuParentMouseEnter.bind(this), listenerOptions);

            menuItem.addEventListener('mouseleave', this._onSubmenuParentMouseLeave.bind(this), listenerOptions);

            menuItem.addEventListener('keydown', this._onSubmenuKeyDown.bind(this), listenerOptions);

            menuItem
                .querySelector('[data-submenu-toggle]')
                .addEventListener('click', this._onSubmenuToggleClick.bind(this), listenerOptions);
        });

        this._primaryNav.querySelectorAll('[data-menu-item]').forEach((menuItem) => {
            menuItem
                .closest('[data-menu]')
                .addEventListener('focusin', this._onSubmenuFocusIn.bind(this), listenerOptions);
            menuItem
                .closest('[data-menu]')
                .addEventListener('focusout', this._onSubmenuFocusOut.bind(this), listenerOptions);
        });

        window.addEventListener('resize', this._onResize.bind(this), listenerOptions);

        $(window).on('changed.zf.mediaquery', (event, newBreakpoint, oldBreakpoint) => {
            // the event is triggered also on initial loading, where no breakpoints are passed in
            if (!newBreakpoint || !oldBreakpoint) {
                return;
            }

            const queries = Foundation.MediaQuery.queries,
                breakpointIndex = queries.findIndex((q) => q.name === this._breakpoint),
                newBreakpointIndex = queries.findIndex((q) => q.name === newBreakpoint),
                oldBreakpointIndex = queries.findIndex((q) => q.name === oldBreakpoint);

            // when switching between mobile and desktop menus, sanitize the DOM
            if (
                (oldBreakpointIndex < breakpointIndex && newBreakpointIndex >= breakpointIndex) ||
                (oldBreakpointIndex >= breakpointIndex && newBreakpointIndex < breakpointIndex)
            ) {
                this._primaryNav.querySelectorAll('[data-menu][style]').forEach((menu) => {
                    menu.style.height = '';
                    menu.style.display = '';
                });

                this._primaryNav.querySelectorAll('[data-menu-item][data-expanded="true"]').forEach((menuItem) => {
                    menuItem.classList.remove('is-expanded');
                    menuItem.removeAttribute('data-expanded');
                });
            }
        });
    }

    /** @private */
    _toggleSubmenu(parentMenuItem) {
        if (parentMenuItem.dataset.expanded === 'true') {
            this._closeSubmenu(parentMenuItem);
        } else {
            this._openSubmenu(parentMenuItem);
        }

        if (Foundation.MediaQuery.atLeast(this._breakpoint) === false) {
            this._sanitizeHeights();
        }
    }

    /** @private */
    _checkAlignment(parentMenuItem) {
        let menuParentDimensions = parentMenuItem.getBoundingClientRect(),
            subMenu = parentMenuItem.querySelector('[data-menu-sub]'),
            subMenuDimensions = subMenu.getBoundingClientRect();

        if (menuParentDimensions.left + subMenuDimensions.width > window.innerWidth) {
            parentMenuItem.classList.add('aligns-right');
        } else {
            parentMenuItem.classList.remove('aligns-right');
        }
    }

    /** @private */
    _sanitizeHeights() {
        if (this._subMenus === null) {
            // cache and reverse the submenus.
            // reversing is required to first handle the deepest submenus
            this._subMenus = [...this._primaryNav.querySelectorAll('[data-menu-sub]')].reverse();
        }

        this._subMenus.forEach((subMenu) => {
            if (subMenu.parentElement.dataset.expanded === 'true') {
                // Iterate through children and sum their heights to correctly get the intended height of the submenu.
                // This is required as the submenus' heights are transitioned by CSS and therefore report wrong height
                // when queried directly.
                subMenu.style.height =
                    Array.prototype.map
                        .call(subMenu.children, (child) => {
                            let val;

                            if ('submenuToggle' in child.dataset) {
                                val = 0; // toggle buttons are inline with the link, so they must not count
                            } else if (
                                'menuItem' in child.dataset &&
                                'hasSubmenu' in child.dataset &&
                                child.dataset.expanded === 'true'
                            ) {
                                val = Array.prototype.map
                                    .call(child.children, (grandChild) => {
                                        let val;

                                        if ('submenuToggle' in grandChild.dataset) {
                                            val = 0;
                                        } else {
                                            val = grandChild.scrollHeight;
                                        }

                                        return val;
                                    })
                                    .reduce((prv, cur) => prv + cur);
                            } else if (
                                'menuItem' in child.dataset &&
                                'hasSubmenu' in child.dataset &&
                                child.dataset.expanded !== 'true'
                            ) {
                                val = child.querySelector('a').scrollHeight;
                            } else {
                                val = child.scrollHeight;
                            }

                            return val;
                        })
                        .reduce((prv, cur) => prv + cur) + 'px';
            } else {
                subMenu.style.height = '0px';
            }
        });
    }

    /** @private */
    _openSubmenu(parentMenuItem) {
        const toggleButton = parentMenuItem.querySelector('[data-submenu-toggle]');

        toggleButton.setAttribute('aria-expanded', true);
        toggleButton.querySelector('[data-submenu-toggle-label]').innerHTML =
            toggleButton.dataset.submenuToggleHideText;

        parentMenuItem.classList.add('is-expanded');
        parentMenuItem.dataset.expanded = 'true';

        if (Foundation.MediaQuery.atLeast(this._breakpoint)) {
            this._checkAlignment(parentMenuItem);
        } else {
            const subMenu = parentMenuItem.querySelector('[data-menu-sub]');

            if (subMenu) {
                subMenu.style.display = 'block';
            }
        }

        // close other submenus immediately, if they are no ancestors
        [...this._openMenus]
            .filter((menu) => menu.contains(parentMenuItem) === false)
            .forEach(this._closeSubmenu.bind(this));

        this._openMenus.add(parentMenuItem);
    }

    /** @private */
    _closeSubmenu(parentMenuItem) {
        const toggleButton = parentMenuItem.querySelector('[data-submenu-toggle]');

        toggleButton.setAttribute('aria-expanded', false);
        toggleButton.querySelector('[data-submenu-toggle-label]').innerHTML =
            toggleButton.dataset.submenuToggleShowText;

        if (Foundation.MediaQuery.atLeast(this._breakpoint)) {
            parentMenuItem.classList.remove('is-expanded');
            parentMenuItem.dataset.expanded = 'false';
        } else {
            const subMenu = parentMenuItem.querySelector('[data-menu-sub]');

            if (subMenu) {
                parentMenuItem.classList.remove('is-expanded');
                parentMenuItem.dataset.expanded = 'false';

                const listenerOptions = eventListenerOptions({ once: true }),
                    listener = () => {
                        if (parentMenuItem.dataset.expanded === 'false') {
                            subMenu.style.display = 'none';
                        }

                        if (listenerOptions.once !== true) {
                            // IE legacy
                            subMenu.removeEventListener('transitionend', listener, listenerOptions);
                        }
                    };

                subMenu.removeEventListener('transitionend', listener, listenerOptions);
                subMenu.addEventListener('transitionend', listener, listenerOptions);
            }
        }

        clearTimeout(this._subMenuClosingTimeouts.get(parentMenuItem));

        this._openMenus.delete(parentMenuItem);
    }

    /** @private */
    _onSubmenuParentTouchStart() {
        this._touch = true;
    }

    /** @private */
    _onSubmenuParentMouseEnter(event) {
        if (this._touch) {
            this._touch = false;
            return;
        }

        const parentMenuItem = event.target.closest('[data-menu-item][data-has-submenu]');

        event.stopImmediatePropagation();

        clearTimeout(this._subMenuClosingTimeouts.get(parentMenuItem));

        this._openSubmenu(parentMenuItem);
    }

    /** @private */
    _onSubmenuParentMouseLeave(event) {
        if (this._touch) {
            return;
        }

        const parentMenuItem = event.target.closest('[data-menu-item][data-has-submenu]');

        event.stopImmediatePropagation();

        this._scheduleClosing(parentMenuItem);
    }

    /** @private */
    _onSubmenuKeyDown(event) {
        event.stopImmediatePropagation();

        if (event.key === 'Escape') {
            const parentMenuItem = event.target.closest('[data-menu-item][data-has-submenu][data-expanded="true"]');

            if (parentMenuItem) {
                this._closeSubmenu(parentMenuItem);

                parentMenuItem.querySelector('[data-submenu-toggle]').focus();
            }
        }
    }

    /** @private */
    _onSubmenuFocusIn(event) {
        event.stopImmediatePropagation();

        if (Foundation.MediaQuery.atLeast(this._breakpoint) === false) {
            return;
        }

        clearTimeout(this._subMenuClosingTimeouts.get(event.target.closest('[data-menu-item][data-has-submenu]')));
    }

    /** @private */
    _onSubmenuFocusOut(event) {
        event.stopImmediatePropagation();

        if (Foundation.MediaQuery.atLeast(this._breakpoint) === false) {
            return;
        }

        // requestAnimationFrame is necessary to wait for document.activeElement to be updated
        requestAnimationFrame(() => {
            let prevMenu = event.target.closest('[data-menu-item][data-has-submenu]'),
                focusedMenu = document.activeElement;

            // if the focus left the primary navigation component completely, close all open menus
            if (focusedMenu && this._primaryNav !== focusedMenu && this._primaryNav.contains(focusedMenu) === false) {
                this._openMenus.forEach((parentMenuItem) => {
                    this._closeSubmenu(parentMenuItem);
                });
            } else {
                while (prevMenu) {
                    if (prevMenu && prevMenu !== focusedMenu && prevMenu.contains(focusedMenu) === false) {
                        this._closeSubmenu(prevMenu); //.closest('[data-menu-item][data-has-submenu]'));
                    }

                    prevMenu = prevMenu.parentElement.closest('[data-menu-item]');
                }
            }
        });
    }

    /** @private */
    _onSubmenuToggleClick(event) {
        const parentMenuItem = event.target.closest('[data-menu-item][data-has-submenu]');

        event.stopImmediatePropagation();

        this._toggleSubmenu(parentMenuItem);
    }

    /** @private */
    _scheduleClosing(parentMenuItem) {
        this._subMenuClosingTimeouts.set(
            parentMenuItem,
            setTimeout(() => {
                this._closeSubmenu(parentMenuItem);
            }, this._closingDelay),
        );
    }

    /** @private */
    _onResize() {
        if (Foundation.MediaQuery.atLeast(this._breakpoint)) {
            this._openMenus.forEach((menu) => {
                this._checkAlignment(menu);
            });
        }
    }
}
